import { ReactNode, useRef, useState } from "react"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import Card from "./Card"
import { interpolate } from "../../../domain/CMS/utils"
import Label from "./Label"
import plusIcon from "../../../assets/plus-icon.svg"
import minusIcon from "../../../assets/minus-icon.svg"
import cn from "../../../utils/classes"
import RowContainer from "./Layout/RowContainer"
import { Typography, typographyTypes } from "./Typography"
import { Popover } from "./Popover"
import Icon from "./Icon"

interface ExpandableCardProps {
  title: string
  tooltipText?: string
  children: ReactNode
  descriptionComponents?: ReactNode
  labelStyleOverides?: string
  cardStyleOverride?: string
  buttonStyleOverrides?: string
  required?: boolean
  isDisabled?: boolean
  defaultOpen?: boolean
  isOpen?: boolean // Controlled state prop
  onToggle?: (isOpen: boolean) => void // Callback for state changes
  useChevron?: boolean
}

const ExpandableCard: React.FC<React.PropsWithChildren<ExpandableCardProps>> = (
  props,
) => {
  const {
    defaultOpen,
    isOpen,
    onToggle,
    title,
    required,
    isDisabled,
    labelStyleOverides,
    cardStyleOverride,
    buttonStyleOverrides,
    descriptionComponents,
    tooltipText,
    useChevron = false,
    children,
  } = props
  const { cms: cmsContent } = useCMSReady(["common"])
  const common = cmsContent.common

  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen)
  const tooltipPopoverRef = useRef<HTMLDivElement | null>(null)

  // Use controlled state if provided, otherwise use internal state
  const isOpenState = isOpen !== undefined ? isOpen : internalIsOpen

  const handleToggle = () => {
    const newIsOpen = !isOpenState
    if (onToggle) {
      onToggle(newIsOpen)
    } else {
      setInternalIsOpen(newIsOpen)
    }
  }

  const buttonToLabelId = title.toLowerCase().replace("", "-")

  const isExpanded = !isDisabled && isOpenState
  const contentWrapperStyle = cardStyleOverride ?? "w-full"

  return (
    <RowContainer styleOverrides="w-full">
      <Card
        rounded={true}
        borderStyleOverride="border-solid border-gray-200 border-1/4 items-start"
        styleOverrides={cn(cardStyleOverride, {
          "hover:bg-[#F4FBFD] p-0 px-4 min-h-[44px]": !isExpanded,
          "pt-0": isExpanded,
        })}
      >
        <div
          role="button"
          aria-disabled={isDisabled}
          onClick={!isDisabled ? handleToggle : undefined}
          className={cn(
            "flex flex-row gap-2 items-center justify-between h-[44px]",
            {
              "mb-2": isExpanded,
              "cursor-pointer": !isDisabled,
            },
            buttonStyleOverrides,
          )}
          aria-label={interpolate(common?.accordionExpandSectionAriaLabelText, {
            title,
          })}
        >
          <div className="flex flex-row gap-2 items-center">
            <Label
              text={title}
              isRequired={required}
              htmlFor={buttonToLabelId}
              styleOverrides={cn("text-gray-700", labelStyleOverides, {
                "cursor-pointer": !isDisabled,
              })}
            />
            {tooltipText && (
              <Popover
                scrollElementRef={tooltipPopoverRef}
                elementPosition="right"
                triggerElement={
                  <Icon
                    iconType="infoQuestionmarkLightGray"
                    styleOverrides="w-4 h-4"
                  />
                }
                styleOverrides="flex items justify-center p-1"
                containerStyleOverrides="m-0 p-0"
              >
                <Card
                  isTooltipCard={true}
                  tooltipPosition="right"
                  styleOverrides="max-w-sm w-full"
                >
                  <Typography
                    variant={typographyTypes.NORMAL}
                    text={tooltipText}
                    styleOverrides="text-gray-900"
                  />
                </Card>
              </Popover>
            )}
          </div>

          <div className="flex flex-col items-center justify-center">
            {useChevron ? (
              <div
                className={cn("flex flex-col items-center justify-center", {
                  "rotate-180": !isDisabled && isOpenState,
                })}
              >
                <Icon
                  id={`${isExpanded ? "collapse" : "expand"}-icon`}
                  iconType="chevronDown"
                  size="md"
                />
              </div>
            ) : (
              <img
                id={`${isExpanded ? "collapse" : "expand"}-icon`}
                width={20}
                height={20}
                src={isExpanded ? minusIcon : plusIcon}
              />
            )}
          </div>
        </div>
        <div className={contentWrapperStyle}>
          {!isDisabled && isOpenState && descriptionComponents && (
            <div className="mb-2">{descriptionComponents}</div>
          )}
          {!isDisabled && isOpenState && children}
        </div>
      </Card>
    </RowContainer>
  )
}

export default ExpandableCard
